import { useNavigate } from "react-router";
import { BackButton } from "@/components/BackButton";
import { PageTransition } from "@/components/PageTransition";
import { useEffectOnce } from "@/hooks/useEffectOnce";
import { track } from "@/lib/analytics/track";
import {
  pruneEntries,
  usePhotoHistoryStore,
  type PhotoHistoryEntry,
} from "@/lib/store/photoHistoryStore";
import {
  daysOld,
  formatExpiry,
  remainingFraction,
  stubParts,
} from "@/lib/utils/photoHistory";
import { useSEO } from "@/lib/seo/useSEO";

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { entries } = usePhotoHistoryStore();
  const visibleEntries = pruneEntries(entries);
  const now = Date.now();

  useSEO({
    title: "My Photos - Recent Sessions",
    description:
      "Reopen your recent photo booth sessions from this device. Photos are kept here for 3 days.",
    keywords: "my photos, photo history, recent sessions, KeptScene",
  });

  useEffectOnce(() => {
    track("history_page_view", { entry_count: visibleEntries.length });
    if (visibleEntries.length === 0) {
      track("history_empty_view", {});
    }
  });

  const handleEntryClick = (entry: PhotoHistoryEntry) => {
    track("history_entry_click", {
      entry_count: visibleEntries.length,
      days_old: daysOld(entry.createdAt, Date.now()),
    });
    navigate(`/result?jobId=${entry.jobId}`);
  };

  return (
    <PageTransition className="min-h-screen overflow-y-hidden">
      <BackButton prevUrl="/" />
      <div className="px-8 py-6">
        <div className="bg-[url('/tape.png')] bg-cover bg-center w-full text-center -rotate-6">
          <h1 className="text-[40px] font-family-reenie text-vintage-black mb-8">
            MY PHOTOS
          </h1>
        </div>
        <p className="text-center font-family-reenie opacity-80 text-vintage-red text-[20px]">
          * Kept on this phone for 3 days — reopen any photo to download it
          again.
        </p>

        {visibleEntries.length === 0 ? (
          <div className="border border-vintage-black bg-vintage-pink p-[18px] text-center mt-4">
            <p className="font-family-reenie text-[28px]">
              No photos saved on this device yet.
            </p>
            <p className="font-family-kanit text-[13px] mt-2">
              Photos you take are saved here for 3 days.
            </p>
          </div>
        ) : (
          visibleEntries.map((entry) => {
            const stub = stubParts(entry.createdAt);
            return (
              <button
                key={entry.jobId}
                type="button"
                onClick={() => handleEntryClick(entry)}
                className="flex bg-[#FFFBF1] border border-vintage-black text-left w-full mt-[14px] cursor-pointer"
              >
                <span className="w-[86px] shrink-0 bg-vintage-black text-[#FFFBF1] px-2 py-2.5 text-center flex flex-col items-center justify-center gap-0.5">
                  <span className="text-3xl leading-none">{stub.day}</span>
                  <span className="font-family-kanit text-[11px] tracking-wider">
                    {stub.month}
                  </span>
                  <span className="font-family-kanit text-[11px] tracking-wider">
                    {stub.time}
                  </span>
                </span>
                <span className="flex-1 border-l border-dashed border-vintage-black p-3 min-w-0">
                  <span className="block font-family-reenie text-[28px] leading-tight">
                    {entry.frameName ?? "Photo session"}
                  </span>
                  <span className="block font-family-kanit text-xs text-[#6f655c] mt-1">
                    <span className="inline-block font-bold text-[11px] px-2.5 py-[3px] border border-vintage-black bg-[#b9d3a8] text-vintage-black">
                      Ready
                    </span>{" "}
                    · {formatExpiry(entry.createdAt, now)}
                  </span>
                  <span
                    className="block h-2 border border-vintage-black bg-white relative mt-2"
                    aria-hidden="true"
                  >
                    <span
                      className="absolute inset-y-0 left-0 bg-vintage-steal"
                      style={{
                        width: `${Math.round(remainingFraction(entry.createdAt, now) * 100)}%`,
                      }}
                    />
                  </span>
                  <span className="flex gap-2 mt-2.5">
                    <span className="inline-flex items-center justify-center min-h-[44px] px-[18px] py-2.5 border border-vintage-black bg-vintage-red text-white font-family-kanit font-bold text-sm">
                      View
                    </span>
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </PageTransition>
  );
};
